/* eslint-disable */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    TextField,
    Typography,
    Box,
    Paper,
    Stack,
    IconButton,
    Autocomplete,
    Divider,
    Switch,
    FormControlLabel,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    RadioGroup,
    Radio,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Grid as MuiGrid
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useProvince } from 'hooks/api/terminology/province/useProvince';
import { useDistrict } from 'hooks/api/terminology/district/useDistrict';
import useSite from 'hooks/api/terminology/site/useSite';
import useVenue from 'hooks/api/terminology/venue/useVenue';

const ALLOWANCE_RATES = {
    'Breakfast': 5,
    'Lunch': 15,
    'Dinner': 25,
    'Incidental': 5,
    'Proved Acc': 100,
    'Unproved Acc': 45,
    'No Acc Required': 0
};

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

const VenueSelectionModal = ({ 
    open, 
    onClose, 
    date, 
    formik, 
    provinces, 
    districts, 
    sites, 
    venues,
    handleVenueTypeChange,
    handleProvinceChange,
    handleDistrictChange,
    handleVenueChange
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const currentAllowances = formik.values.dailyAllowances?.[date] || {};
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle sx={{ 
                bgcolor: '#213363', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <LocationOnIcon />
                <Typography variant="h6">
                    Select Location for {formatDate(date)}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Stack spacing={3}>
                    <FormControl component="fieldset">
                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#213363' }}>
                            Location Type
                        </Typography>
                        <RadioGroup
                            row
                            value={currentAllowances.venueType || 'site'}
                            onChange={(e) => handleVenueTypeChange(date, e.target.value)}
                            sx={{
                                '& .MuiFormControlLabel-root': {
                                    marginRight: 2,
                                    '& .MuiRadio-root': {
                                        color: '#213363',
                                        '&.Mui-checked': {
                                            color: '#213363',
                                        }
                                    }
                                }
                            }}
                        >
                            <FormControlLabel 
                                value="site" 
                                control={<Radio size="small" />} 
                                label={
                                    <Typography variant="body2" sx={{ color: '#213363' }}>
                                        Site
                                    </Typography>
                                }
                            />
                            <FormControlLabel 
                                value="venue" 
                                control={<Radio size="small" />} 
                                label={
                                    <Typography variant="body2" sx={{ color: '#213363' }}>
                                        Venue
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    {currentAllowances.venueType === 'site' ? (
                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: '#213363' }}>Province</InputLabel>
                                <Select
                                    value={currentAllowances.provinceId || ''}
                                    onChange={(e) => handleProvinceChange(date, e.target.value)}
                                    label="Province"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        }
                                    }}
                                >
                                    {provinces.map((province) => (
                                        <MenuItem key={province.id} value={province.id}>
                                            {province.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl 
                                fullWidth 
                                size="small"
                                disabled={!currentAllowances.provinceId}
                            >
                                <InputLabel sx={{ color: '#213363' }}>District</InputLabel>
                                <Select
                                    value={currentAllowances.districtId || ''}
                                    onChange={(e) => handleDistrictChange(date, e.target.value)}
                                    label="District"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        }
                                    }}
                                >
                                    {districts.map((district) => (
                                        <MenuItem key={district.id} value={district.id}>
                                            {district.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl 
                                fullWidth 
                                size="small"
                                disabled={!currentAllowances.districtId}
                            >
                                <InputLabel sx={{ color: '#213363' }}>Site</InputLabel>
                                <Select
                                    value={currentAllowances.venueId || ''}
                                    onChange={(e) => handleVenueChange(date, e.target.value)}
                                    label="Site"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#213363',
                                        }
                                    }}
                                >
                                    {sites.map((site) => (
                                        <MenuItem key={site.id} value={site.id}>
                                            {site.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    ) : (
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: '#213363' }}>Venue</InputLabel>
                            <Select
                                value={currentAllowances.venueId || ''}
                                onChange={(e) => handleVenueChange(date, e.target.value)}
                                label="Venue"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#213363',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#213363',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#213363',
                                    }
                                }}
                            >
                                {venues.map((venue) => (
                                    <MenuItem key={venue.id} value={venue.id}>
                                        {venue.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {currentAllowances.venue && (
                        <Box sx={{ 
                            p: 2,
                            bgcolor: '#e8f0fe',
                            borderRadius: 1,
                            border: '1px solid #213363'
                        }}>
                            <Typography variant="subtitle2" sx={{ color: '#213363', mb: 1 }}>
                                Selected Location:
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#213363' }}>
                                {currentAllowances.venue}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    sx={{ 
                        color: '#213363',
                        borderColor: '#213363',
                        '&:hover': {
                            borderColor: '#213363',
                            bgcolor: 'rgba(33, 51, 99, 0.04)'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DailyAllowanceMatrix = ({ dates, formik }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [sameVenue, setSameVenue] = useState(false);
    const [lastSelectedVenue, setLastSelectedVenue] = useState(null);
    const { listProvince, provinces } = useProvince();
    const { districts, fetchDistrictsByProvince } = useDistrict();
    const { sites, fetchSiteByDistrict } = useSite();
    const { venues, listVenues } = useVenue();
    const [selectedProvince, setSelectedProvince] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [venueType, setVenueType] = useState('site'); // 'site' or 'venue'
    const [selectedDate, setSelectedDate] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Initialize dailyAllowances if they don't exist
    useEffect(() => {
        const currentValues = formik.values.dailyAllowances || {};
        
        // Create a new object with all dates initialized
        const initializedAllowances = dates.reduce((acc, date) => {
            acc[date] = {
                ...(currentValues[date] || {}),
                'Breakfast': currentValues[date]?.Breakfast || false,
                'Lunch': currentValues[date]?.Lunch || false,
                'Dinner': currentValues[date]?.Dinner || false,
                'Incidental': currentValues[date]?.Incidental || false,
                'Proved Acc': currentValues[date]?.['Proved Acc'] || false,
                'Unproved Acc': currentValues[date]?.['Unproved Acc'] || false,
                'No Acc Required': currentValues[date]?.['No Acc Required'] || false,
                'miscAmount': currentValues[date]?.miscAmount || '0.00',
                'venue': currentValues[date]?.venue || '',
                'provinceId': currentValues[date]?.provinceId || '',
                'districtId': currentValues[date]?.districtId || '',
                'venueId': currentValues[date]?.venueId || '',
                'venueType': currentValues[date]?.venueType || 'site'
            };
            return acc;
        }, {});

        // Set the entire dailyAllowances object at once
        formik.setFieldValue('dailyAllowances', initializedAllowances);
    }, [dates]);

    useEffect(() => {
        listProvince(0, 100);
        listVenues(0, 1000);
    }, [listProvince, listVenues]);

    const handleVenueTypeChange = (date, type) => {
        const currentAllowances = { ...formik.values.dailyAllowances };
        if (!currentAllowances[date]) {
            currentAllowances[date] = {};
        }
        
        currentAllowances[date] = {
            ...currentAllowances[date],
            venueType: type,
            provinceId: '',
            districtId: '',
            venueId: '',
            venue: ''
        };
        
        formik.setFieldValue('dailyAllowances', currentAllowances);
        
        if (sameVenue) {
            dates.slice(dates.indexOf(date) + 1).forEach(nextDate => {
                if (!currentAllowances[nextDate]) {
                    currentAllowances[nextDate] = {};
                }
                currentAllowances[nextDate] = {
                    ...currentAllowances[nextDate],
                    venueType: type,
                    provinceId: '',
                    districtId: '',
                    venueId: '',
                    venue: ''
                };
            });
            formik.setFieldValue('dailyAllowances', currentAllowances);
        }
    };

    const handleProvinceChange = (date, provinceId) => {
        const currentAllowances = { ...formik.values.dailyAllowances };
        if (!currentAllowances[date]) {
            currentAllowances[date] = {};
        }
        
        currentAllowances[date] = {
            ...currentAllowances[date],
            provinceId,
            districtId: '',
            venueId: '',
            venue: ''
        };
        
        formik.setFieldValue('dailyAllowances', currentAllowances);
        fetchDistrictsByProvince(provinceId);
        
        if (sameVenue) {
            dates.slice(dates.indexOf(date) + 1).forEach(nextDate => {
                if (!currentAllowances[nextDate]) {
                    currentAllowances[nextDate] = {};
                }
                currentAllowances[nextDate] = {
                    ...currentAllowances[nextDate],
                    provinceId,
                    districtId: '',
                    venueId: '',
                    venue: ''
                };
            });
            formik.setFieldValue('dailyAllowances', currentAllowances);
        }
    };

    const handleDistrictChange = (date, districtId) => {
        const currentAllowances = { ...formik.values.dailyAllowances };
        if (!currentAllowances[date]) {
            currentAllowances[date] = {};
        }
        
        currentAllowances[date] = {
            ...currentAllowances[date],
            districtId,
            venueId: '',
            venue: ''
        };
        
        formik.setFieldValue('dailyAllowances', currentAllowances);
        fetchSiteByDistrict(districtId);
        
        if (sameVenue) {
            dates.slice(dates.indexOf(date) + 1).forEach(nextDate => {
                if (!currentAllowances[nextDate]) {
                    currentAllowances[nextDate] = {};
                }
                currentAllowances[nextDate] = {
                    ...currentAllowances[nextDate],
                    districtId,
                    venueId: '',
                    venue: ''
                };
            });
            formik.setFieldValue('dailyAllowances', currentAllowances);
        }
    };

    const handleVenueChange = (date, venueId) => {
        const currentAllowances = { ...formik.values.dailyAllowances };
        if (!currentAllowances[date]) {
            currentAllowances[date] = {};
        }
        
        const venueType = currentAllowances[date].venueType;
        const selectedItem = venueType === 'site' 
            ? sites.find(site => site.id === venueId)
            : venues.find(venue => venue.id === venueId);
        
        const venueName = selectedItem ? selectedItem.name : '';
        
        currentAllowances[date] = {
            ...currentAllowances[date],
            venueId,
            venue: venueName
        };
        
        formik.setFieldValue('dailyAllowances', currentAllowances);
        
        if (sameVenue) {
            dates.slice(dates.indexOf(date) + 1).forEach(nextDate => {
                if (!currentAllowances[nextDate]) {
                    currentAllowances[nextDate] = {};
                }
                currentAllowances[nextDate] = {
                    ...currentAllowances[nextDate],
                    venueId,
                    venue: venueName
                };
            });
            formik.setFieldValue('dailyAllowances', currentAllowances);
        }
    };

    const allowanceTypes = [
        'Breakfast',
        'Lunch',
        'Dinner',
        'Incidental',
        'Proved Acc',
        'Unproved Acc',
        'No Acc Required'
    ];

    const handleCheckboxChange = (date, type) => {
        const fieldName = `dailyAllowances.${date}.${type}`;
        formik.setFieldValue(fieldName, !formik.values.dailyAllowances?.[date]?.[type]);

        // If selecting an accommodation type, unselect others
        if (['Proved Acc', 'Unproved Acc', 'No Acc Required'].includes(type)) {
            const accommodationTypes = ['Proved Acc', 'Unproved Acc', 'No Acc Required'];
            accommodationTypes.forEach(accType => {
                if (accType !== type) {
                    formik.setFieldValue(`dailyAllowances.${date}.${accType}`, false);
                }
            });
        }
    };

    const handleMiscAmountChange = (date, value) => {
        formik.setFieldValue(`dailyAllowances.${date}.miscAmount`, value);
    };

    // Function to handle same venue toggle
    const handleSameVenueToggle = (event) => {
        const isEnabled = event.target.checked;
        setSameVenue(isEnabled);
        
        if (isEnabled && lastSelectedVenue) {
            const currentAllowances = { ...formik.values.dailyAllowances };
            const firstVenueDate = dates.find(date => currentAllowances[date]?.venue);
            
            if (firstVenueDate) {
                const startIndex = dates.indexOf(firstVenueDate);
                const venueToApply = currentAllowances[firstVenueDate].venue;
                
                dates.slice(startIndex + 1).forEach(date => {
                    if (!currentAllowances[date]) {
                        currentAllowances[date] = {};
                    }
                    currentAllowances[date] = {
                        ...currentAllowances[date],
                        venue: venueToApply
                    };
                });
                
                formik.setFieldValue('dailyAllowances', currentAllowances);
            }
        }
    };

    // Calculate total amounts for each date and overall total
    const { dailyTotals, grandTotal } = useMemo(() => {
        const totals = {};
        let overall = 0;

        dates.forEach(date => {
            let dateTotal = 0;
            const allowances = formik.values.dailyAllowances?.[date] || {};

            // Sum up all checked allowances
            Object.entries(allowances).forEach(([type, isChecked]) => {
                if (isChecked && ALLOWANCE_RATES[type]) {
                    dateTotal += ALLOWANCE_RATES[type];
                }
            });

            // Add miscellaneous amount
            if (allowances.miscAmount) {
                dateTotal += parseFloat(allowances.miscAmount) || 0;
            }

            totals[date] = dateTotal;
            overall += dateTotal;
        });

        return {
            dailyTotals: totals,
            grandTotal: overall
        };
    }, [dates, formik.values.dailyAllowances]);

    const handleOpenModal = (date) => {
        setSelectedDate(date);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedDate(null);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2 
            }}>
                <Typography variant="h6" gutterBottom>
                    Daily Allowance Matrix
                </Typography>
                <Tooltip title="When enabled, selecting a venue will automatically apply to subsequent dates">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={sameVenue}
                                onChange={handleSameVenueToggle}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ContentCopyIcon sx={{ fontSize: 18, color: '#213363' }} />
                                <Typography variant="body2" sx={{ color: '#213363' }}>
                                    Same Venue
                                </Typography>
                            </Stack>
                        }
                    />
                </Tooltip>
            </Box>
            
            {isMobile ? (
                <Stack spacing={2}>
                    {dates.map((date) => (
                        <Card key={date} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#213363' }}>
                                            {formatDate(date)}
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#213363', fontWeight: 700 }}>
                                            ${dailyTotals[date].toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {allowanceTypes.map((type) => (
                                            <Box
                                                key={`${date}-${type}`}
                                                onClick={() => handleCheckboxChange(date, type)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: formik.values.dailyAllowances?.[date]?.[type] ? '#213363' : '#e0e0e0',
                                                    bgcolor: formik.values.dailyAllowances?.[date]?.[type] ? 'rgba(33, 51, 99, 0.05)' : 'white',
                                                    cursor: 'pointer',
                                                    flexGrow: 1,
                                                    minWidth: '120px'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={formik.values.dailyAllowances?.[date]?.[type] || false}
                                                    size="small"
                                                    sx={{ p: 0, mr: 1, color: '#213363', '&.Mui-checked': { color: '#213363' } }}
                                                />
                                                <Box>
                                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                                        {type}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ${ALLOWANCE_RATES[type]}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                                            Misc Amount
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            value={formik.values.dailyAllowances?.[date]?.miscAmount || ''}
                                            onChange={(e) => handleMiscAmountChange(date, e.target.value)}
                                            InputProps={{
                                                startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>$</Typography>
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                                            Location
                                        </Typography>
                                        {formik.values.dailyAllowances?.[date]?.venue ? (
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    bgcolor: '#e8f0fe',
                                                    borderRadius: 1,
                                                    border: '1px solid #213363',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleOpenModal(date)}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <LocationOnIcon sx={{ color: '#213363', fontSize: 20 }} />
                                                    <Typography variant="body2" sx={{ color: '#213363', fontWeight: 500 }}>
                                                        {formik.values.dailyAllowances[date].venue}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        ) : (
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LocationOnIcon />}
                                                onClick={() => handleOpenModal(date)}
                                                sx={{
                                                    color: '#213363',
                                                    borderColor: '#213363',
                                                    justifyContent: 'flex-start'
                                                }}
                                            >
                                                Select Location
                                            </Button>
                                        )}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell 
                                    sx={{ 
                                        bgcolor: '#213363',
                                        color: 'white',
                                        minWidth: '150px'
                                    }}
                                >
                                    Date
                                </TableCell>
                                {allowanceTypes.map((type) => (
                                    <TableCell 
                                        key={type} 
                                        align="center"
                                        sx={{ 
                                            bgcolor: '#213363',
                                            color: 'white',
                                            minWidth: '120px'
                                        }}
                                    >
                                        {type}
                                        <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                            (${ALLOWANCE_RATES[type]})
                                        </Typography>
                                    </TableCell>
                                ))}
                                <TableCell 
                                    sx={{ 
                                        bgcolor: '#213363',
                                        color: 'white',
                                        minWidth: '150px'
                                    }}
                                >
                                    Misc Amount
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        bgcolor: '#213363',
                                        color: 'white',
                                        minWidth: '250px'
                                    }}
                                >
                                    Venue {sameVenue && <Typography variant="caption" sx={{ ml: 1, color: 'rgba(255,255,255,0.7)' }}>(Auto-filling enabled)</Typography>}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        bgcolor: '#213363',
                                        color: 'white',
                                        minWidth: '120px'
                                    }}
                                >
                                    Daily Total
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dates.map((date) => (
                                <TableRow 
                                    key={date}
                                    sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: '#f8f8f8',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                        }
                                    }}
                                >
                                    <TableCell>
                                        {formatDate(date)}
                                    </TableCell>
                                    {allowanceTypes.map((type) => (
                                        <TableCell 
                                            key={`${date}-${type}`}
                                            align="center"
                                        >
                                            <Checkbox
                                                checked={formik.values.dailyAllowances?.[date]?.[type] || false}
                                                onChange={() => handleCheckboxChange(date, type)}
                                                size="small"
                                                sx={{
                                                    color: '#213363',
                                                    '&.Mui-checked': {
                                                        color: '#213363',
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={formik.values.dailyAllowances?.[date]?.miscAmount || ''}
                                            onChange={(e) => handleMiscAmountChange(date, e.target.value)}
                                            InputProps={{ 
                                                inputProps: { 
                                                    min: 0,
                                                    style: { textAlign: 'right', paddingRight: '8px' }
                                                },
                                                startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>$</Typography>
                                            }}
                                            sx={{ width: '160px' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={1}>
                                            {formik.values.dailyAllowances?.[date]?.venue ? (
                                                <Box sx={{ 
                                                    p: 1,
                                                    bgcolor: '#e8f0fe',
                                                    borderRadius: 1,
                                                    border: '1px solid #213363',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: '#e3eaf2'
                                                    }
                                                }}
                                                onClick={() => handleOpenModal(date)}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <LocationOnIcon sx={{ color: '#213363', fontSize: 20 }} />
                                                        <Typography variant="body2" sx={{ color: '#213363' }}>
                                                            {formik.values.dailyAllowances[date].venue}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<LocationOnIcon />}
                                                    onClick={() => handleOpenModal(date)}
                                                    sx={{
                                                        color: '#213363',
                                                        borderColor: '#213363',
                                                        '&:hover': {
                                                            borderColor: '#213363',
                                                            bgcolor: 'rgba(33, 51, 99, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    Select Location
                                                </Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#213363' }}>
                                        ${dailyTotals[date].toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <VenueSelectionModal
                open={modalOpen}
                onClose={handleCloseModal}
                date={selectedDate}
                formik={formik}
                provinces={provinces}
                districts={districts}
                sites={sites}
                venues={venues}
                handleVenueTypeChange={handleVenueTypeChange}
                handleProvinceChange={handleProvinceChange}
                handleDistrictChange={handleDistrictChange}
                handleVenueChange={handleVenueChange}
            />

            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mt: 2,
                p: 2,
                bgcolor: '#f8f8f8',
                borderRadius: 1
            }}>
                <Typography variant="h6" sx={{ color: '#213363', fontWeight: 600 }}>
                    Total Amount: ${grandTotal.toFixed(2)}
                </Typography>
            </Box>
        </Box>
    );
};

DailyAllowanceMatrix.propTypes = {
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    formik: PropTypes.object.isRequired
};

export default DailyAllowanceMatrix; 